import React, { useEffect, useState } from "react";
import "../../pages/IndexPage/IndexPage.css";

/*
  Reseñas de Google.

  Para mostrar reseñas REALES de Google (y que se actualicen solas en cada
  carga de la página) hay que configurar dos cosas:

    1) Una API key de Google Maps con "Maps JavaScript API" + "Places API"
       habilitadas. Crea un archivo  .env  en la raíz del proyecto (junto a
       package.json) con:

           REACT_APP_GOOGLE_MAPS_API_KEY=tu_api_key_aqui

       y reinicia el servidor (npm start). Restringe la key a tu dominio en
       la consola de Google Cloud.

    2) El PLACE_ID de Sun Taka. Sácalo del buscador oficial:
       https://developers.google.com/maps/documentation/places/web-service/place-id
       y pégalo abajo en la constante PLACE_ID.

  Mientras no estén configurados, se muestran las reseñas de respaldo de abajo
  para que la sección no quede vacía. Solo se muestran reseñas de 4 y 5
  estrellas, las más recientes primero.

  Nota: la API de Google devuelve como máximo 5 reseñas por consulta.
*/

const PLACE_ID = ""; // <-- PEGA AQUÍ EL PLACE ID DE SUN TAKA
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const FALLBACK_REVIEWS = [
  {
    author_name: "Wesley Jampel",
    rating: 5,
    relative_time_description: "hace 1 semana",
    text: "Taka makes some of the best, authentic sushi in Barcelona if not the best!",
  },
  {
    author_name: "Hamilton Soares",
    rating: 5,
    relative_time_description: "hace 3 semanas",
    text: "Um restaurante simplesmente maravilhoso. Tivemos uma noite fantástica!",
  },
  {
    author_name: "Teresa BR",
    rating: 5,
    relative_time_description: "hace 1 mes",
    text: "Calidad y servicio muy bueno.",
  },
];

const AVATAR_COLORS = [
  "#7b3ff2",
  "#e0457b",
  "#e8833a",
  "#2e9e7b",
  "#3a7be0",
  "#c0392b",
];

function GoogleG() {
  // Logo de Google en SVG (sin depender de ninguna imagen).
  return (
    <svg className="review-google" viewBox="0 0 48 48" aria-label="Google">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

function Stars({ rating }) {
  const full = Math.round(rating);
  return (
    <div className="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= full ? "star on" : "star"}>
          ★
        </span>
      ))}
    </div>
  );
}

function GoogleReviews() {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);

  useEffect(() => {
    if (!API_KEY || !PLACE_ID) return; // sin configurar -> reseñas de respaldo
    let cancelled = false;

    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.places) {
          return resolve(window.google);
        }
        const existing = document.getElementById("gmaps-places");
        if (existing) {
          existing.addEventListener("load", () => resolve(window.google));
          existing.addEventListener("error", reject);
          return;
        }
        const s = document.createElement("script");
        s.id = "gmaps-places";
        s.async = true;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&language=es`;
        s.onload = () => resolve(window.google);
        s.onerror = reject;
        document.body.appendChild(s);
      });

    loadScript()
      .then((google) => {
        const svc = new google.maps.places.PlacesService(
          document.createElement("div")
        );
        svc.getDetails(
          { placeId: PLACE_ID, fields: ["reviews"], language: "es" },
          (place, status) => {
            if (cancelled) return;
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              place &&
              place.reviews &&
              place.reviews.length
            ) {
              const positive = place.reviews
                .filter((r) => r.rating >= 4)
                .sort((a, b) => b.time - a.time);
              if (positive.length) setReviews(positive);
            }
          }
        );
      })
      .catch(() => {
        /* si algo falla, se mantienen las reseñas de respaldo */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="reviews-grid">
      {reviews.slice(0, 6).map((r, i) => {
        const initial = (r.author_name || "?").trim().charAt(0).toUpperCase();
        const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
        return (
          <div className="review-card" key={i}>
            <div className="review-head">
              <div className="review-avatar" style={{ backgroundColor: color }}>
                {initial}
              </div>
              <div className="review-meta">
                <span className="review-name">{r.author_name}</span>
                <span className="review-time">
                  {r.relative_time_description}
                </span>
              </div>
              <GoogleG />
            </div>
            <Stars rating={r.rating} />
            <p className="review-text">{r.text}</p>
          </div>
        );
      })}
    </div>
  );
}

export default GoogleReviews;
